import React from "react";

const ELLIPSIS = "ellipsis";
const SIBLING_COUNT = 2; // pages to show on each side of current
const MAX_WITHOUT_ELLIPSIS = 7; // show all pages if totalPages <= this

/**
 * Returns an array of pagination items: numbers (page indices) or ELLIPSIS.
 * e.g. [1, "ellipsis", 5, 6, 7, "ellipsis", 20]
 */
function getPaginationItems(page, totalPages) {
  if (totalPages <= MAX_WITHOUT_ELLIPSIS) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const left = Math.max(2, page - SIBLING_COUNT);
  const right = Math.min(totalPages - 1, page + SIBLING_COUNT);
  const items = [1];
  if (left > 2) items.push(ELLIPSIS);
  for (let n = left; n <= right; n++) {
    if (!items.includes(n)) items.push(n);
  }
  if (right < totalPages - 1) items.push(ELLIPSIS);
  if (totalPages > 1) items.push(totalPages);
  return items;
}

/**
 * Reusable pagination widget.
 * @param {number} page - Current page (1-based)
 * @param {function} onPageChange - (newPage: number) => void
 * @param {number} limit - Page size
 * @param {number} totalCount - Total number of items
 * @param {boolean} [hasNext] - Whether there is a next page (if omitted, derived from totalCount)
 */
const Pagination = ({ page, onPageChange, limit, totalCount, hasNext }) => {
  const totalPages = Math.ceil(totalCount / limit) || 1;
  const hasNextPage = hasNext !== undefined ? hasNext : page * limit < totalCount;
  const from = totalCount === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, totalCount);
  const paginationItems = getPaginationItems(page, totalPages);

  if (totalCount <= 0) return null;

  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={!hasNextPage}
          className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{from}</span> to{" "}
            <span className="font-medium">{to}</span> of{" "}
            <span className="font-medium">{totalCount}</span> results
          </p>
        </div>
        <div>
          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
            <button
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {paginationItems.map((item, idx) =>
              item === ELLIPSIS ? (
                <span
                  key={`ellipsis-${idx}`}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm text-gray-500"
                  aria-hidden="true"
                >
                  …
                </span>
              ) : (
                <button
                  key={item}
                  onClick={() => onPageChange(item)}
                  className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                    item === page
                      ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {item}
                </button>
              )
            )}
            <button
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={!hasNextPage}
              className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
